import { removalsView } from "./views/removals";
import { campaignsView } from "./views/campaigns";
import { spendingView } from "./views/spending";
import { aiAccessView } from "./views/aiAccess";
import "./ui/tokens.css";
import "./style.css";
import { createTheme } from "./ui/theme-core";
import { register, startRouter } from "./router";
import { getToken, hasSession, signOut, onConnectionEvent } from "./api";
import { accountView } from "./views/account";
import { loginView } from "./views/login";
import { fleetView } from "./views/fleet";
import { addClientView } from "./views/addClient";
import { clientDetailView } from "./views/clientDetail";
import { connectionsView } from "./views/connections";
import { templateStudioView, templatePreviewView, templateUseView } from "./views/studio/studioPage";
import { queueView } from "./views/queue";
import { assistedView } from "./views/assisted";
import { reportsView } from "./views/reports";
import { verificationGlobalView } from "./views/verificationGlobal";
import { importClientsView } from "./views/importClients";
import { clientFactsView } from "./views/facts/factsPage";
import { allLinksView, clientLinksView } from "./views/links/linksView";
import { clientContentView } from "./views/content/contentPage";
import { clientMediaView } from "./views/media/mediaPage";
import { clientSiteKitView } from "./views/sitekit/siteKitPage";
import { websitesView } from "./views/websites";
import { clientWebsitesView } from "./views/clientWebsites";
import { clientActivityView } from "./views/clientActivity";
import { dripFeedView } from "./views/dripFeed";
import { overviewView } from "./views/overview";
import { approvalsView } from "./views/approvals";
import { icon } from "./ui/icons";
import "./ui/shell.css";

const { applyTheme, getStoredTheme, themeToggle } = createTheme("cloudpush_theme");
applyTheme(getStoredTheme());

const appEl = document.getElementById("app")!;
// True only in the owner's local build (`vite build --mode local-app`). A literal comparison so the hosted build drops the local code.
const LOCAL = typeof __CLOUDPUSH_MODE__ !== "undefined" && __CLOUDPUSH_MODE__ === "local";

function guardedView<T extends (...args: any[]) => any>(view: T): T {
  return ((root: HTMLElement, ...rest: any[]) => {
    if (LOCAL && !getToken()) {
      loginView(root);
      return;
    }
    return view(root, ...rest);
  }) as T;
}

function renderShell() {
  appEl.innerHTML = `
    <div class="app-shell">
      <aside class="nav-rail">
        <a class="brand" href="#/" aria-label="CloudPush home"><span class="brand-mark"></span><span>Cloud<span>Push</span></span></a>
        <button type="button" class="mobile-nav-close" aria-label="Close navigation">__CLOSE_ICON__</button>
        <nav class="nav" aria-label="Main navigation">
          <div class="nav-group"><div class="nav-label">Manage</div>
            <a href="#/" data-path="/">__OVERVIEW_ICON__<span>Overview</span></a>
            <a href="#/clients" data-path="/clients">__CLIENT_ICON__<span>Clients</span></a>
            <a href="#/websites" data-path="/websites">__WEBSITE_ICON__<span>Websites</span></a>
            <a href="#/campaigns" data-path="/campaigns">__PUBLISH_ICON__<span>Publishing</span></a>
            <a href="#/approvals" data-path="/approvals">__CHECK_ICON__<span>Approvals</span></a>
            <a href="#/templates" data-path="/templates">__TEMPLATE_ICON__<span>Template Studio</span></a>
            <a href="#/links" data-path="/links">__LINK_ICON__<span>Links Created</span></a>
          </div>
          <div class="nav-group"><div class="nav-label">Operations</div>
            <a href="#/queue" data-path="/queue">__QUEUE_ICON__<span>Queue</span></a>
            <a href="#/delivery-checks" data-path="/delivery-checks">__CHECK_ICON__<span>Delivery checks</span></a>
            <a href="#/assisted" data-path="/assisted">__ASSISTED_ICON__<span>Assisted work</span></a>
          </div>
          <div class="nav-group"><div class="nav-label">Insights & setup</div>
            <a href="#/reports" data-path="/reports">__REPORT_ICON__<span>Reports</span></a>
            <a href="#/connections" data-path="/connections">__CONNECTION_ICON__<span>Connections</span></a>
            <a href="#/ai-access" data-path="/ai-access">__AI_ICON__<span>AI access</span></a>
            <a href="#/spending" data-path="/spending">__SPENDING_ICON__<span>Generation spending</span></a>
          </div>
          <div class="nav-bottom">${LOCAL ? "" : '<a href="#/account"><span>Your account</span></a>'}<a href="#" id="logout"><span>Sign out</span></a></div>
        </nav>
      </aside>
      <div class="app-workspace">
        <header class="app-topbar"><button type="button" class="mobile-nav-toggle" aria-label="Open navigation" aria-expanded="false" aria-controls="primary-navigation">__MENU_ICON__</button><div class="app-context" id="app-context">Client workspace</div><div class="app-top-actions"><div class="theme-slot"></div></div></header>
        <main class="main" id="main"></main>
      </div>
    </div>
  `;
  const nav = appEl.querySelector<HTMLElement>(".nav")!;
  const appShell = appEl.querySelector<HTMLElement>(".app-shell")!;
  nav.id = "primary-navigation";
  // Use the app's own hand-authored SVG set; every icon remains paired with a text label.
  const routeIcons: [string, Parameters<typeof icon>[0]][] = [["/", "chart"], ["/clients", "users"], ["/websites", "monitor"], ["/campaigns", "send"], ["/approvals", "check"], ["/templates", "grid"], ["/links", "link"], ["/queue", "layers"], ["/delivery-checks", "check-circle"], ["/assisted", "file"], ["/reports", "chart"], ["/connections", "plug"], ["/ai-access", "sliders"], ["/spending", "chart"]];
  for (const [path, name] of routeIcons) {
    const a = nav.querySelector<HTMLAnchorElement>(`a[data-path="${path}"]`);
    const label = a?.querySelector("span");
    if (a && label) a.replaceChildren(icon(name, 18), label);
  }
  const logout = nav.querySelector<HTMLAnchorElement>("#logout");
  logout?.prepend(icon("arrow-right", 18));
  appEl.querySelector<HTMLElement>(".app-top-actions .theme-slot")?.append(themeToggle());
  const menuToggle = appEl.querySelector<HTMLButtonElement>(".mobile-nav-toggle");
  const menuClose = appEl.querySelector<HTMLButtonElement>(".mobile-nav-close");
  const workspace = appEl.querySelector<HTMLElement>(".app-workspace")!;
  menuToggle?.replaceChildren(icon("menu", 18));
  menuClose?.replaceChildren(icon("x", 18));
  const setDrawer = (open: boolean, restore = false) => {
    appShell.classList.toggle("mobile-nav-open", open);
    menuToggle?.setAttribute("aria-expanded", String(open));
    menuToggle?.setAttribute("aria-label", "Open navigation");
    workspace.inert = open;
    if (open) menuClose?.focus();
    else if (restore) menuToggle?.focus();
  };
  menuToggle?.addEventListener("click", () => setDrawer(!appShell.classList.contains("mobile-nav-open")));
  menuClose?.addEventListener("click", () => setDrawer(false, true));
  appShell.addEventListener("click", (event) => { if (event.target === appShell && appShell.classList.contains("mobile-nav-open")) setDrawer(false, true); });
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || !appShell.classList.contains("mobile-nav-open")) return;
    setDrawer(false, true);
  });
  appShell.addEventListener("keydown", (event) => {
    if (event.key !== "Tab" || !appShell.classList.contains("mobile-nav-open")) return;
    const focusable = [...appShell.querySelectorAll<HTMLElement>('.nav-rail a[href], .nav-rail button:not([disabled])')].filter((el) => el.offsetParent !== null);
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  nav.querySelectorAll<HTMLAnchorElement>("a[data-path]").forEach((a) => a.addEventListener("click", () => {
    setDrawer(false, true);
  }));
  if (LOCAL) {
    // Connection state (connected / cannot reach the local API / unauthorized) as a slim bar above the page content.
    const bar = document.createElement("div");
    bar.className = "conn-bar";
    const col = document.createElement("div");
    col.className = "main-col";
    const mainEl = appEl.querySelector("main")!;
    mainEl.replaceWith(col);
    col.append(bar, mainEl);
    void import("./views/connectionBadge").then(({ connectionHeader }) => bar.replaceChildren(connectionHeader().el));
  }
  document.getElementById("logout")!.onclick = async (e) => {
    e.preventDefault();
    try { await signOut(); window.location.hash = "/"; window.location.reload(); }
    catch { alert("Could not sign out. Please retry when the connection is restored."); }
  };

  const main = document.getElementById("main")!;

  register("/", guardedView(overviewView));
  register("/overview", guardedView(overviewView));
  register("/clients", guardedView((root: HTMLElement) => fleetView(root)));
  register("/clients/needs-attention", guardedView((root: HTMLElement) => fleetView(root, "needs_attention")));
  register("/websites", guardedView(websitesView));
  register("/clients/new", guardedView(addClientView));
  register("/clients/import", guardedView(importClientsView));
  register("/clients/:id/websites", guardedView(clientWebsitesView));
  register("/clients/:id/drip", guardedView(dripFeedView));
  register("/clients/:id/activity", guardedView(clientActivityView));
  register("/clients/:id", guardedView(clientDetailView));
  register("/clients/:id/removals", guardedView(removalsView));
  register("/clients/:id/facts", guardedView(clientFactsView));
  register("/clients/:id/links", guardedView(clientLinksView));
  register("/clients/:id/content", guardedView(clientContentView));
  register("/clients/:id/media", guardedView(clientMediaView));
  register("/clients/:id/site", guardedView(clientSiteKitView));
  register("/clients/:id/site/:kitId", guardedView(clientSiteKitView));
  register("/links", guardedView(allLinksView));
  register("/connections", guardedView(connectionsView));
  register("/campaigns", guardedView(campaignsView));
  register("/approvals", guardedView(approvalsView));
  register("/spending", guardedView(spendingView));
  register("/ai-access", guardedView(aiAccessView));
  register("/templates", guardedView(templateStudioView));
  register("/templates/preview/:kitId", guardedView(templatePreviewView));
  register("/templates/use/:kitId", guardedView(templateUseView));
  register("/queue", guardedView(queueView));
  register("/assisted", guardedView(assistedView));
  register("/reports", guardedView(reportsView));
  register("/verification", guardedView(verificationGlobalView));
  register("/delivery-checks", guardedView(verificationGlobalView));

  if (!LOCAL) register("/account", guardedView(accountView));
  startRouter(main);
}

async function start() {
  if (LOCAL) {
    // `local:start` prints a login link whose fragment carries the token. Store it and remove it from the address bar first.
    const { consumeTokenFragment } = await import("./connection");
    consumeTokenFragment();
    // The same link opened again in a tab that is already open only changes the fragment (no page load): take it and start over.
    window.addEventListener("hashchange", () => {
      const taken = consumeTokenFragment();
      if (taken === "stored" || taken === "api") window.location.reload();
    });
  }
  // Setup links must be consumed even when another browser session is active.
  if (!LOCAL && new URLSearchParams(window.location.hash.slice(1)).has("setup")) {
    await loginView(appEl); return;
  }
  try {
    if (await hasSession()) {
      renderShell();
      if (!LOCAL) onConnectionEvent(event => {
        if (event === "unauthorized") window.location.reload();
      });
    } else await loginView(appEl);
  } catch {
    await loginView(appEl);
    const error = appEl.querySelector<HTMLElement>("#login-error");
    if (error) { error.hidden = false; error.textContent = "CloudPush is temporarily unavailable. Please try signing in again shortly."; }
  }
}
void start();
